import {
	Auth0Client,
	createAuth0Client,
	LocalStorageCache,
	type Cacheable,
	type ICache,
	type WrappedCacheEntry,
} from "@auth0/auth0-spa-js";
import { z } from "zod";

import * as transport from "../../transport";

export const TokenResponseSchema = z.object({
	access_token: z.string(),
	refresh_token: z.string(),
	id_token: z.string(),
});

type Tokens = z.infer<typeof TokenResponseSchema>;

const clientId = import.meta.env["VITE_AUTH0_CLIENT_ID"];

export const auth0Config = {
	clientId, // payments-sdk-app
	audience: "https://sdk.myzeller.com", // sdk-api
	scope: "openid profile email offline_access",
	get redirectUri(): string {
		return window.location.origin;
	},
	get domain(): string {
		const { host } = window.location;

		/* v8 ignore next 3 -- production host branch; the test runner serves from localhost so window.location.host cannot reach this */
		if (host.endsWith(".com")) {
			return "auth.myzeller.com";
		}

		/* v8 ignore next 3 -- staging host branch; the test runner serves from localhost so window.location.host cannot reach this */
		if (host.endsWith(".show")) {
			return "auth.myzeller.show";
		}

		return "auth.myzeller.dev";
	},
};

export const TOKEN_CACHE_KEY = `@@auth0spajs@@::${clientId}::${auth0Config.audience}::${auth0Config.scope}`;
export const USER_CACHE_KEY = `@@auth0spajs@@::${clientId}::@@user@@`;

export function createTransportInterceptCache(): ICache {
	const inner = new LocalStorageCache();

	return {
		set<T = Cacheable>(key: string, entry: T): void {
			inner.set(key, entry);

			const wrapped = entry as unknown as Partial<WrappedCacheEntry>;
			const userEntry = entry as unknown as { id_token?: unknown };

			const config: {
				authAccessToken?: string;
				authRefreshToken?: string;
				authIdToken?: string;
			} = {};

			if (wrapped?.body?.access_token) {
				config.authAccessToken = wrapped.body.access_token;
			}
			if (wrapped?.body?.refresh_token) {
				config.authRefreshToken = wrapped.body.refresh_token;
			}
			if (wrapped?.body?.id_token) {
				config.authIdToken = wrapped.body.id_token;
			} else if (typeof userEntry?.id_token === "string") {
				config.authIdToken = userEntry.id_token;
			}

			if (Object.keys(config).length > 0) {
				transport.Config(config);
			}
		},

		get<T = Cacheable>(key: string) {
			return inner.get<T>(key);
		},

		remove(key: string): void {
			inner.remove(key);
		},

		allKeys(): string[] {
			return inner.allKeys();
		},
	};
}

export function getAuth0Client(): Promise<Auth0Client> {
	return createAuth0Client({
		clientId: auth0Config.clientId,
		domain: auth0Config.domain,
		useRefreshTokens: true,
		cache: createTransportInterceptCache(),
		authorizationParams: {
			redirect_uri: auth0Config.redirectUri,
			scope: auth0Config.scope,
			audience: auth0Config.audience,
		},
	});
}

export function readTokenCache(): Tokens | undefined {
	const tokensRaw = window.localStorage.getItem(TOKEN_CACHE_KEY);
	const userRaw = window.localStorage.getItem(USER_CACHE_KEY);

	if (!tokensRaw || !userRaw) {
		return undefined;
	}

	try {
		const tokens = (JSON.parse(tokensRaw) as WrappedCacheEntry).body;
		const user = JSON.parse(userRaw) as { id_token?: string };

		const result = TokenResponseSchema.safeParse({
			access_token: tokens.access_token,
			refresh_token: tokens.refresh_token,
			id_token: user.id_token,
		});

		return result.success ? result.data : undefined;
	} catch {
		return undefined;
	}
}
